import React from 'react';
import Styled from 'styled';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/client/react/hooks';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import ResponsiveImageView from 'lib/images/responsive-image-view';
import FormattedDateAgoView from 'lib/intl/formatted-date-ago-view';

import users from 'four-nets/icons/svg/icon_users.svg';
import refresh from 'four-nets/icons/svg/icon_refresh.svg';
import message from 'four-nets/icons/svg/icon_comment.svg';
import envelope from 'four-nets/icons/svg/icon_message-3.svg';
import picture from 'four-nets/icons/svg/icon_picture.svg';

import { Site } from 'four-nets/site';
import CheckboxView from 'four-nets/ui/form/checkbox-view';
import ActionButtonView from 'four-nets/ui/action-button/action-button-view';

import repostButtonStyles from './repost-button-view.scss';
import defaultStyles from './products-table-row-view.scss';
import noPhotoStyles from './products-table-row-view-nophoto.scss';
import mobileStatIcon from './products-table-row-view-mobilestaticon.scss';
import refreshIconStyles from './products-table-row-view-refreshicon.scss';
import { ProductsTableRowFragment } from './products-table-row-fragment.graphql';
import { ProductStatus } from './products-table-row-fragment.graphql.types';
import mutation, {
    SellerRepostProductMutation,
    SellerRepostProductMutationVariables,
} from './seller-repost-product-mutation.graphql';

export interface Props extends ProductsTableRowFragment {
    styles: typeof defaultStyles;
    myBazaarType: string;
    checked?: boolean;
    handleChange: (target: HTMLInputElement) => void;
}

const ProductsTableRowView: React.FC<Props> = (props, { site = {} } = {}) => {
    const {
        styles,
        myBazaarType,
        id,
        url,
        slug,
        title,
        price,
        startTime,
        isRepostable,
        isHidden,
        status,
        buyersCount,
        messagesCount,
        viewsCount,
        newMessagesCount,
        productPhotoFiles,
        latestUnreadBuyerUsername,
    } = props;

    const [activate] = useMutation<SellerRepostProductMutation, SellerRepostProductMutationVariables>(mutation);

    const photoFile =
        productPhotoFiles &&
        productPhotoFiles.edges.length > 0 &&
        productPhotoFiles.edges[0]!.node &&
        productPhotoFiles.edges[0]!.node.photofile;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        props.handleChange(event.target);
    };

    const repost = () => {
        if (isRepostable) {
            return activate({
                variables: {
                    input: {
                        productId: id,
                    },
                },
            });
        }

        return Promise.resolve();
    };

    return (
        <tr className={styles.tableRow}>
            <td className={styles.checkbox}>
                <CheckboxView value={id} onChange={handleChange} />
            </td>
            <td className={styles.imageWrapper}>
                {photoFile ? (
                    <ResponsiveImageView aspectFill={true} image={photoFile} />
                ) : (
                    <SVGIcon style={{ image: noPhotoStyles.noPhoto }} src={picture} />
                )}
            </td>
            <td className={styles.tableCellName}>
                <div className={styles.marginRight}>
                    <Link
                        to={`/market/${slug}`}
                        className={classnames(styles.nameLinkView, {
                            [styles.deletedColor]:
                                isHidden ||
                                status === ProductStatus.STATUS_DELETED ||
                                status === ProductStatus.STATUS_BLOCKED,
                        })}
                    >
                        {title}
                        {isHidden || status === ProductStatus.STATUS_DELETED ? (
                            <div className={styles.deletedLabel}>
                                <FormattedMessage id='STATUS_DELETED' />
                            </div>
                        ) : status === ProductStatus.STATUS_EXPIRED ? (
                            <div className={styles.deletedLabel}>
                                <FormattedMessage id='STATUS_EXPIRED' />
                            </div>
                        ) : status === ProductStatus.STATUS_INACTIVE ? (
                            <div className={styles.deletedLabel}>
                                <FormattedMessage id='STATUS_INACTIVE' />
                            </div>
                        ) : status === ProductStatus.STATUS_BLOCKED ? (
                            <div className={styles.deletedLabel}>
                                <FormattedMessage id='STATUS_BLOCKED' />
                            </div>
                        ) : null}
                    </Link>
                </div>
                <div className={styles.mobileStat}>
                    <SVGIcon
                        style={{ image: mobileStatIcon.mobileStatIcon }}
                        src={myBazaarType !== 'wishlist' ? message : users}
                    />
                    {myBazaarType !== 'wishlist' ? messagesCount : buyersCount}
                    {myBazaarType !== 'wishlist' && newMessagesCount > 0 ? (
                        <span className={styles.highlight}>
                            {newMessagesCount}&nbsp;
                            <FormattedMessage id='BAZAAR_NEW_MESSAGES_COUNT' values={{ count: newMessagesCount }} />
                        </span>
                    ) : null}
                </div>
                <div className={styles.mobileStat}>
                    <SVGIcon style={{ image: mobileStatIcon.mobileStatIcon }} src={envelope} />
                    {price > 0 ? `${price} ${site && site.currency}` : <FormattedMessage id='GIVE_FOR_FREE' />}
                </div>
            </td>
            <td className={styles.tableCell}>
                {price > 0 ? `${price} ${site && site.currency}` : <FormattedMessage id='GIVE_FOR_FREE' />}
            </td>
            <td className={styles.tableCell}>
                {myBazaarType !== 'wishlist' ? messagesCount : buyersCount}
                {myBazaarType !== 'wishlist' && newMessagesCount > 0 ? (
                    <Link to={`${url}?username=${latestUnreadBuyerUsername}`} className={styles.highlight}>
                        {newMessagesCount}&nbsp;
                        <FormattedMessage id='BAZAAR_NEW_MESSAGES_COUNT' values={{ count: newMessagesCount }} />
                    </Link>
                ) : null}
            </td>
            <td className={styles.tableCell}>
                {viewsCount || 0}&nbsp;
                <FormattedMessage id='BAZAAR_VIEWS_COUNT' values={{ count: viewsCount || 0 }} />
            </td>
            <td className={styles.tableCell}>
                <FormattedDateAgoView value={new Date(startTime)} />
            </td>
            {myBazaarType === 'selling' ? (
                <td className={styles.repost}>
                    <div className={styles.button}>
                        <ActionButtonView styles={repostButtonStyles} disabled={!isRepostable} onClick={repost}>
                            <span className={styles.desktopOnly}>
                                <FormattedMessage id='BAZAAR_REPOST_INFORMAL' />
                            </span>
                            <SVGIcon style={{ image: refreshIconStyles.refreshIcon }} src={refresh} />
                        </ActionButtonView>
                    </div>
                </td>
            ) : null}
        </tr>
    );
};

ProductsTableRowView.contextTypes = {
    site: PropTypes.instanceOf(Site),
};

export default Styled(defaultStyles)(ProductsTableRowView);
